export const API_ERROR_CODES = {
    CLIENT_NETWORK_ERROR: 0,
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_ALLOWED: 405,
    REQUEST_TIMEOUT: 408,
    CONFLICT: 409,
    TOO_MANY_REQUESTS: 429,
    INTERNAL_SERVER_ERROR: 500,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
    GATEWAY_TIMEOUT: 504,
} as const

// Error message keys for internationalization
export const API_ERROR_KEYS = {
    NETWORK_ERROR: 'API_ERRORS.NETWORK_ERROR',
    TIMEOUT_ERROR: 'API_ERRORS.TIMEOUT_ERROR',
    CONNECTION_LOST: 'API_ERRORS.CONNECTION_LOST',

    FETCH_FAILED: 'API_ERRORS.FETCH_FAILED',
    SAVE_FAILED: 'API_ERRORS.SAVE_FAILED',
    UPDATE_FAILED: 'API_ERRORS.UPDATE_FAILED',
    DELETE_FAILED: 'API_ERRORS.DELETE_FAILED',
    CREATE_FAILED: 'API_ERRORS.CREATE_FAILED',

    UNAUTHORIZED: 'API_ERRORS.UNAUTHORIZED',
    SESSION_EXPIRED: 'API_ERRORS.SESSION_EXPIRED',
    ACCESS_DENIED: 'API_ERRORS.ACCESS_DENIED',

    INVALID_DATA: 'API_ERRORS.INVALID_DATA',
    REQUIRED_FIELDS: 'API_ERRORS.REQUIRED_FIELDS',
    INVALID_FORMAT: 'API_ERRORS.INVALID_FORMAT',

    SERVER_ERROR: 'API_ERRORS.SERVER_ERROR',
    SERVICE_UNAVAILABLE: 'API_ERRORS.SERVICE_UNAVAILABLE',
    INTERNAL_ERROR: 'API_ERRORS.INTERNAL_ERROR',

    INVALID_RESPONSE: 'API_ERRORS.INVALID_RESPONSE',
    MALFORMED_DATA: 'API_ERRORS.MALFORMED_DATA',
    UNEXPECTED_RESPONSE: 'API_ERRORS.UNEXPECTED_RESPONSE',

    NOT_FOUND: 'API_ERRORS.NOT_FOUND',
    RESOURCE_UNAVAILABLE: 'API_ERRORS.RESOURCE_UNAVAILABLE',
    RESOURCE_CONFLICT: 'API_ERRORS.RESOURCE_CONFLICT',

    TOO_MANY_REQUESTS: 'API_ERRORS.TOO_MANY_REQUESTS',
    RATE_LIMIT_EXCEEDED: 'API_ERRORS.RATE_LIMIT_EXCEEDED',

    UNKNOWN_ERROR: 'API_ERRORS.UNKNOWN_ERROR',
    TRY_AGAIN_LATER: 'API_ERRORS.TRY_AGAIN_LATER',
} as const

// Fallback error messages (for backward compatibility and when i18n is not available)
export const API_ERRORS = {
    NETWORK_ERROR:
        'Unable to connect to the server. Please check your internet connection and try again.',
    TIMEOUT_ERROR: 'The request timed out. Please try again.',
    CONNECTION_LOST: 'Connection lost. Please check your network and retry.',

    FETCH_FAILED: 'Failed to load data. Please try again.',
    SAVE_FAILED: 'Failed to save data. Please try again.',
    UPDATE_FAILED: 'Failed to update data. Please try again.',
    DELETE_FAILED: 'Failed to delete data. Please try again.',
    CREATE_FAILED: 'Failed to create data. Please try again.',

    UNAUTHORIZED: 'You are not authorized to perform this action.',
    SESSION_EXPIRED: 'Your session has expired. Please login again.',
    ACCESS_DENIED: 'Access denied. You do not have permission to access this resource.',

    INVALID_DATA: 'The provided data is invalid. Please check and try again.',
    REQUIRED_FIELDS: 'Please fill in all required fields.',
    INVALID_FORMAT: 'The data format is invalid. Please check and try again.',

    SERVER_ERROR: 'A server error occurred. Please try again later.',
    SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again later.',
    INTERNAL_ERROR: 'An internal error occurred. Please contact support if the problem persists.',

    INVALID_RESPONSE: 'Invalid response received from server. Please try again.',
    MALFORMED_DATA: 'The server returned malformed data. Please try again.',
    UNEXPECTED_RESPONSE: 'Received unexpected response from server. Please try again.',

    NOT_FOUND: 'The requested resource was not found.',
    RESOURCE_UNAVAILABLE: 'The requested resource is currently unavailable.',
    RESOURCE_CONFLICT: 'A conflict occurred with the requested resource.',

    TOO_MANY_REQUESTS: 'Too many requests. Please wait a moment and try again.',
    RATE_LIMIT_EXCEEDED: 'Rate limit exceeded. Please wait before making more requests.',

    UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
    TRY_AGAIN_LATER: 'Something went wrong. Please try again later.',
    USER_ID_NOT_AVAILABLE: 'User ID not available.',
    PRODUCT_DETAILS_NOT_FOUND: 'Product details not found. Please verify the order ID.',
    PROPOSAL_HEADER_NOT_FOUND: 'Proposal header data not found. Please verify the order ID.',
    APPROVAL_TIMELINE_NOT_FOUND: 'Approval timeline data not found. Please verify the order ID.',
    YEAR_COMPARISON_NOT_FOUND:
        'Year comparison data not found. Please verify the customer code and year.',
    PAYMENT_INSTALLMENT_NOT_FOUND:
        'Payment installment data not found. Please verify the order ID.',
    ORGANISATION_NOT_FOUND: 'Customer details not found. Please verify the customer code.',
    NO_ORGANISATION_DATA_FOUND: 'No customer data found in API response',
    INVALID_ORGANISATION_CODE_BASE: 'Invalid customer code:',
    TERRITORY_NOT_FOUND: 'Territory information not found for this customer.',
    ORDER_NOT_FOUND: 'Order not found. Please verify the order ID.',
    APPROVAL_PERMISSION_DENIED: 'You do not have permission to approve this proposal.',
    REJECTION_PERMISSION_DENIED: 'You do not have permission to reject this proposal.',
    NO_REPORTING_USERS_FOUND: 'No reporting users found for the specified user',
    DATA_SAVED_SUCCESSFULLY: 'Data saved successfully',
    UNABLE_TO_SAVE: 'Unable to save',
} as const

export const API_ERROR_HELPERS = {
    getErrorByStatusCode(statusCode: number): string {
        switch (statusCode) {
            case API_ERROR_CODES.BAD_REQUEST:
                return API_ERRORS.INVALID_DATA
            case API_ERROR_CODES.UNAUTHORIZED:
                return API_ERRORS.UNAUTHORIZED
            case API_ERROR_CODES.FORBIDDEN:
                return API_ERRORS.ACCESS_DENIED
            case API_ERROR_CODES.NOT_FOUND:
                return API_ERRORS.NOT_FOUND
            case API_ERROR_CODES.REQUEST_TIMEOUT:
                return API_ERRORS.TIMEOUT_ERROR
            case API_ERROR_CODES.CONFLICT:
                return API_ERRORS.RESOURCE_CONFLICT
            case API_ERROR_CODES.TOO_MANY_REQUESTS:
                return API_ERRORS.TOO_MANY_REQUESTS
            case API_ERROR_CODES.INTERNAL_SERVER_ERROR:
                return API_ERRORS.SERVER_ERROR
            case API_ERROR_CODES.BAD_GATEWAY:
            case API_ERROR_CODES.SERVICE_UNAVAILABLE:
            case API_ERROR_CODES.GATEWAY_TIMEOUT:
                return API_ERRORS.SERVICE_UNAVAILABLE
            case API_ERROR_CODES.CLIENT_NETWORK_ERROR:
                return API_ERRORS.NETWORK_ERROR
            default:
                return API_ERRORS.UNKNOWN_ERROR
        }
    },
} as const
